import { modalController } from '@ionic/core';
import { Component, Event, EventEmitter, Fragment, h, Host, Prop, State } from '@stencil/core';
import { FileDoc, FileType, PhotoDoc } from 'common';
import { ChangeEvent } from 'src/global/ConcourseDb';
import { User } from 'src/global/models';
import { FileService } from 'src/global/service/FileService';

enum Tabs {
  TIMELINE = 'timeline',
  ALBUMS = 'albums',
}

@Component({
  tag: 'photos-selector-modal',
  styleUrls: ['../../global/app.css'],
  shadow: true,
})
export class PhotosSelectorModal {
  static async present(projectId: string): Promise<PhotoDoc[]> {
    const modal = await modalController.create({
      component: 'photos-selector-modal',
      cssClass: 'concourse-modal modal-size-90',
      componentProps: { projectId },
      showBackdrop: true,
    });

    modal.present();

    return new Promise<PhotoDoc[]>(resolve => {
      modal.addEventListener('selectAll', async (e: CustomEvent) => {
        resolve(e.detail);
        modal.dismiss();
      });

      modal.addEventListener('dismiss', () => {
        resolve(null);
        modal.dismiss();
      });
    });
  }

  @Prop() projectId: string;

  @State() files: FileDoc[] = [];
  @State() album: FileDoc;
  @State() tab: string = Tabs.TIMELINE;

  @State() selected: PhotoDoc[] = [];
  @State() searchTerm: string = '';

  @Event() dismiss: EventEmitter;
  @Event() selectAll: EventEmitter<PhotoDoc[]>;

  get user(): User {
    return globalThis.user;
  }

  get pageTitle(): string {
    if (this.tab == Tabs.ALBUMS) {
      if (this.album) return this.album.name || 'Album';
      return 'Albums';
    }
    return 'Photos';
  }

  exitAlbum() {
    this.tab = Tabs.ALBUMS;
  }

  async componentWillLoad() {
    const db = FileService.getFilesDb(this.projectId);
    db.on('complete', (docs: FileDoc[]) => {
      this.parseDocs(docs);
    });
    db.on('change', async (e: ChangeEvent) => {
      this.parseDocs(await db.getDocs());
    });
    await db.syncRemote();
    this.parseDocs(await db.getDocs());
  }

  parseDocs(docs: FileDoc[]) {
    this.files = docs.filter(f => f.type == FileType.PHOTO || f.type == FileType.ALBUM);
  }

  onSearchInput(e: CustomEvent<string>) {
    this.searchTerm = e.detail.toLowerCase();
  }

  navigateToTab(tab: string) {
    console.log('onSegmentClick: ', tab);
    this.tab = tab;
  }

  toggleSelected(photos: PhotoDoc[]) {
    photos.forEach(photo => {
      if (this.selected.indexOf(photo) == -1) {
        this.selected = [...this.selected, photo];
      } else {
        this.selected = this.selected.filter(p => p._id != photo._id);
      }
    });
  }

  clearSelection() {
    this.selected = [];
  }

  render() {
    const photos = this.files.filter(f => f.type == FileType.PHOTO && !f.deleted_by) as PhotoDoc[];
    const filesNotDeleted = this.files.filter(f => !f.deleted_by);
    return (
      <Host class="concourse-modal modal-size-90">
        <modal-header
          modalTitle="Photo Selector"
          enableSearch={true}
          showBack={this.tab === Tabs.ALBUMS && !!this.album}
          onDismiss={() => this.dismiss.emit()}
          onBack={() => this.exitAlbum()}
          onSearchInput={e => this.onSearchInput(e as CustomEvent<string>)}
        >
          <ion-buttons>
            {this.tab === Tabs.ALBUMS && (
              <ion-button fill="clear" onClick={() => this.navigateToTab(Tabs.TIMELINE)}>
                <ion-icon slot="icon-only" name="time-outline"></ion-icon>
              </ion-button>
            )}
            {this.tab === Tabs.TIMELINE && (
              <ion-button fill="clear" onClick={() => this.navigateToTab(Tabs.ALBUMS)}>
                <ion-icon slot="icon-only" src="/assets/icon/photo-album-icon.svg"></ion-icon>
              </ion-button>
            )}
          </ion-buttons>
        </modal-header>
        <div class="cc-scrollbars modal-content ion-padding-horizontal">
          {this.tab == Tabs.TIMELINE && (
            <photos-timeline
              projectId={this.projectId}
              photos={photos}
              searchTerm={this.searchTerm}
              selected={this.selected}
              showCheckboxes={true}
              showUploadOptions={false}
              showSelectPhoto={true}
              onToggleSelected={e => this.toggleSelected(e.detail)}
              onSelectPhoto={e => this.selectAll.emit([e.detail])}
            ></photos-timeline>
          )}
          {this.tab == Tabs.ALBUMS && (
            <photos-albums
              projectId={this.projectId}
              files={filesNotDeleted}
              searchTerm={this.searchTerm}
              selected={this.selected}
              showCheckboxes={true}
              showCreateAlbum={false}
              onToggleSelected={e => this.toggleSelected([e.detail])}
            ></photos-albums>
          )}
        </div>
        <ion-footer>
          <ion-toolbar>
            {this.selected.length > 0 && (
              <ion-button slot="start" shape="round" fill="outline" onClick={() => (this.selected = [])}>
                <ion-icon slot="start" name="close-outline"></ion-icon>
                <ion-label>clear Selection</ion-label>
              </ion-button>
            )}
            <ion-button
              data-testid="confirm-photo-selection"
              slot="end"
              shape="round"
              fill="solid"
              color="primary"
              disabled={this.selected.length == 0}
              onClick={() => this.selectAll.emit(this.selected)}
            >
              <ion-label>
                select {this.selected.length} photo{this.selected.length > 1 && 's'}
              </ion-label>
            </ion-button>
          </ion-toolbar>
        </ion-footer>
      </Host>
    );
  }
}
